//
// panel/index.tsx — mounts the in-page Preact app that owns the
// launcher button and the panel it opens.
//
// We mount a single Preact tree into a container div that we own.
// That keeps all of the embed's UI inside one render root, so
// Preact can handle diffing / unmounting cleanly and we don't have
// to juggle imperative DOM across launcher + panel lifecycle.
//
import { render } from 'preact';
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { LicenseInfo } from '@mve/protocol';
import { Panel } from './Panel';
import { launcherButtonStyle } from './styles';

//
// Reference to the container div we appended to <body>, kept in
// module scope so mountLauncher is idempotent.
//
let rootEl: HTMLElement | null = null;

//
// Public entry point from main.ts. Called once after the recorder
// activates via ?mapvideo=1.
//
export function mountLauncher(license: LicenseInfo): void {
  if (rootEl) return;
  injectScrollbarStyles();
  rootEl = document.createElement('div');
  document.body.appendChild(rootEl);
  render(<LauncherApp license={license} />, rootEl);
}

//
// Inject a small stylesheet that restyles the scrollbars on
// elements tagged with `.mve-scroll` so they blend with the dark
// panel chrome instead of showing the OS default (which looks
// glaringly bright against our dark blue). Covers both
// Chromium/WebKit (via ::-webkit-scrollbar pseudo-elements) and
// Firefox (scrollbar-color / scrollbar-width). Idempotent: keyed
// on a fixed id so repeated calls don't stack duplicate styles.
//
function injectScrollbarStyles(): void {
  if (document.getElementById('mve-styles')) return;
  const style = document.createElement('style');
  style.id = 'mve-styles';
  style.textContent = `
.mve-scroll { scrollbar-color: #374151 transparent; scrollbar-width: thin; }
.mve-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
.mve-scroll::-webkit-scrollbar-track { background: transparent; }
.mve-scroll::-webkit-scrollbar-thumb { background: #374151; border-radius: 4px; }
.mve-scroll::-webkit-scrollbar-thumb:hover { background: #4b5563; }
.mve-scroll::-webkit-scrollbar-corner { background: transparent; }
`;
  document.head.appendChild(style);
}

//
// The top-level app: a launcher button and a Panel. The launcher
// unmounts when the panel is open, because the panel now occupies
// the same top-right corner of the viewport and would otherwise
// bleed through. The Panel itself is hidden via `display: none`
// rather than unmounted, so the user's settings (initial view,
// action list, timeline, fade envelope, scrub position) survive
// close/reopen cycles — those live in Panel's Preact hooks, and a
// fresh mount would reset them.
//
// Side effect of this arrangement: the onInstance subscription
// and WebCodecs check run once at page load (when LauncherApp
// first renders) rather than on launcher click. That's fine — both
// are idempotent and cheap.
//
function LauncherApp({ license }: { license: LicenseInfo }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!open && (
        <button style={launcherButtonStyle} onClick={() => setOpen(true)}>
          ● Record Map Video
        </button>
      )}
      <div style={{ display: open ? 'block' : 'none' }}>
        <Panel license={license} onClose={() => setOpen(false)} />
      </div>
    </>
  );
}
